import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { createVisit } from "../actions/visitActions";
import { Col, Form, Row, Button } from "react-bootstrap";
import { notify } from "./notify";
import {
  FaCalculator,
  FaCashRegister,
  FaClock,
  FaRegCalendarAlt,
} from "react-icons/fa";
import { FaHandHoldingDollar } from "react-icons/fa6";
import { SlBasket } from "react-icons/sl";
import Message from "./Message";
import MyLines from "./MyLines";
import { AiOutlineSend } from "react-icons/ai";
import Loader from "./Loader";

const VisitSecreataryForm = ({ reqDate, patientId }) => {
  const dispatch = useDispatch();

  // Safely parse reqDate
  const getInitialDate = () => {
    if (!reqDate) {
      return new Date();
    }

    const date = new Date(reqDate);

    if (isNaN(date.getTime())) {
      return new Date();
    }

    return date;
  };

  const initialDate = getInitialDate();
  const [visitDay, setVisitDay] = useState(format(initialDate, "yyyy-MM-dd"));
  const [visitTime, setVisitTime] = useState(format(initialDate, "HH:mm"));
  const [visitDate, setVisitDate] = useState("");

  // Selected services
  const [visitServices, setVisitServices] = useState([]);

  // Paid amount
  const [paid, setPaid] = useState("0");

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const clinicDetails = useSelector((state) => state.clinicDetails);
  const { clinic } = clinicDetails;

  const visitAddStore = useSelector((state) => state.visitAddStore);
  const { loading, error } = visitAddStore;

  // Create visitDate
  useEffect(() => {
    if (!visitDay || !visitTime) {
      setVisitDate("");
      return;
    }

    const date = new Date(`${visitDay}T${visitTime}`);

    if (isNaN(date.getTime())) {
      setVisitDate("");
      return;
    }

    setVisitDate(date.toISOString());
  }, [visitDay, visitTime]);

  // Calculate total
  const totalAmount = (clinic?.clinicServices || [])
    .filter((service) =>
      visitServices.some((item) => item.serviceId === service._id),
    )
    .reduce((total, service) => total + Number(service.price || 0), 0);

  // Calculate remaining
  const remaining = Math.max(totalAmount - Number(paid || 0), 0);

  // Select / unselect service
  const handleServiceChange = (service, checked) => {
    if (checked) {
      setVisitServices((prev) => {
        if (prev.some((item) => item.serviceId === service._id)) {
          return prev;
        }

        return [
          ...prev,
          {
            idx: Math.random(),
            serviceId: service._id,
          },
        ];
      });
    } else {
      setVisitServices((prev) =>
        prev.filter((item) => item.serviceId !== service._id),
      );
    }
  };

  // Submit visit
  const pushNewVisit = () => {
    if (
      !visitDate ||
      !patientId ||
      !clinic?._id ||
      visitServices.length === 0
    ) {
      notify({
        title: "Register Err",
        message: <p className="lead">All inputs are required.</p>,
        type: "Danger",
        duration: 3000,
      });
      return;
    }

    dispatch(
      createVisit({
        visitDate,
        patientId,
        clinicId: clinic._id,
        clinicOwnerId: clinic.clinicOwnerId,
        secretarySection: {
          secretaryId: userInfo._id,
          visitServices,
          paid: Number(paid || 0),
        },
      }),
    );
  };

  return (
    <>
      <div
        className="border border-primary rounded p-2 p-md-3"
        style={{ borderColor: "#00abff" }}
      >
        {/* ================= DATE / TIME ================= */}
        <Row className="justify-content-center text-center">
          <Col xs={12} sm={6} md={4} lg={6} className="mb-3">
            <Form.Group controlId="visitTime">
              <Form.Label>
                <FaClock /> ساعة الزيارة
              </Form.Label>

              <Form.Control
                type="time"
                className="text-center"
                value={visitTime}
                onChange={(e) => setVisitTime(e.target.value)}
              />
            </Form.Group>
          </Col>{" "}
          <Col xs={12} sm={6} md={4} lg={6} className="mb-3">
            <Form.Group controlId="visitDay">
              <Form.Label>
                <FaRegCalendarAlt /> يوم الزيارة
              </Form.Label>

              <Form.Control
                type="date"
                className="text-center"
                value={visitDay}
                onChange={(e) => setVisitDay(e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
        <MyLines type="dashed" />
        {/* ================= SERVICES ================= */}
        <Row className="mt-2 text-center">
          <Col xs={12}>
            <Form.Label className="fw-bold d-block mb-3">
              <SlBasket /> اختر الخدمات
            </Form.Label>

            <Row className="justify-content-center g-2">
              {clinic?.clinicServices?.filter((s) => !s.isHide).length > 0 ? (
                clinic.clinicServices
                  .filter((s) => !s.isHide)
                  .map((service) => {
                    const isSelected = visitServices.some(
                      (item) => item.serviceId === service._id,
                    );

                    return (
                      <Col
                        onClick={(e) =>
                          handleServiceChange(service, e.target.checked)
                        }
                        key={service._id}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        xl={2}
                      >
                        <div
                          className={`border rounded p-2 h-100 ${
                            isSelected ? "border-primary bg-light" : ""
                          }`}
                        >
                          <Form.Check
                            type="checkbox"
                            id={`service-${service._id}`}
                            checked={isSelected}
                            label={
                              <span className="d-block text-center">
                                {service.label}
                                <br />
                                <small className="text-muted">
                                  {service.price}
                                </small>
                              </span>
                            }
                          />
                        </div>
                      </Col>
                    );
                  })
              ) : (
                <Col xs={12}>
                  <div className="text-muted py-3">لا توجد خدمات</div>
                </Col>
              )}
            </Row>
          </Col>
        </Row>
        <MyLines type="dashed" />

        {/* ================= PAYMENT ================= */}
        <Row className="justify-content-center flex-row-reverse text-center mt-4 g-3">
          {/* Total */}
          <Col xs={12} sm={6} md={4} lg={3}>
            <Form.Group controlId="totalAmount">
              <Form.Label>
                <FaCalculator /> الإجمالي
              </Form.Label>

              <Form.Control
                type="number"
                className="text-center fw-bold"
                value={totalAmount}
                readOnly
              />
            </Form.Group>
          </Col>

          {/* Paid */}
          <Col xs={12} sm={6} md={4} lg={3}>
            <Form.Group controlId="paid">
              <Form.Label>
                <FaCashRegister /> المدفوع
              </Form.Label>

              <Form.Control
                type="number"
                value={paid}
                min="0"
                max={totalAmount}
                className="text-center"
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === "" || Number(value) <= totalAmount) {
                    setPaid(value);
                  }
                }}
              />
            </Form.Group>
          </Col>

          {/* Remaining */}
          <Col xs={12} sm={6} md={4} lg={3}>
            <Form.Group controlId="remaining">
              <Form.Label>
                {" "}
                <FaHandHoldingDollar /> المتبقي
              </Form.Label>

              <Form.Control
                type="number"
                className="text-center fw-bold"
                value={remaining}
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        {/* ================= SUBMIT ================= */}

        {error && (
          <Message children={`Create Visit Err: ${error}`} variant={`danger`} />
        )}

        {loading ? (
          <Loader />
        ) : (
          <Row className="mt-4">
            <Col xs={12} className="text-center">
              <Button
                style={{ backgroundColor: "#00abff" }}
                className="w-100 w-md-auto px-5"
                onClick={pushNewVisit}
                disabled={!visitDate || !patientId || !clinic?._id}
              >
                إنشاء الزيارة <AiOutlineSend />{" "}
              </Button>
            </Col>
          </Row>
        )}
      </div>
    </>
  );
};

export default VisitSecreataryForm;
